import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Search,

  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  ImageIcon,
  Gamepad2,
  RefreshCcw,
  MoreHorizontal,
  DollarSign,
  ZoomIn,
  ThumbsUp,
  SlidersHorizontal,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Pagination,
  FilterPanel,
} from '@/components/ui/Table';
import { Modal, DropdownMenu, Tabs } from '@/components/ui/Modal';
import {
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatCurrency,
  getInitials,
  cn,
} from '@/lib/utils';
import toast from 'react-hot-toast';
import { gamesApi } from '@/lib/api';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface Dispute {
  _id: string;
  gameId: string;
  gameType: string;
  roomType: string;
  entryFee: number;
  prizePool: number;
  status: 'pending' | 'under_review' | 'resolved' | 'rejected';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reason: string;
  resolution?: string;
  resolutionNote?: string;
  reporter: { _id: string; name: string; phone: string; avatar: string | null };
  opponent: { _id: string; name: string; phone: string; avatar: string | null };
  claimedWinner: string;
  screenshots: { id: string; url: string; uploadedBy: string; uploadedAt: string }[];
  messages: { id: string; userId: string; message: string; createdAt: string }[];
  gameStartedAt: string;
  gameEndedAt: string;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedByName?: string;
  assignedTo?: string | null;
  assignedToName?: string;
}

const getStatusConfig = (status: string) => {
  const configs: Record<string, { color: string; bgColor: string; icon: any; label: string }> = {
    pending: { color: 'text-amber-400', bgColor: 'bg-amber-500/10 border-amber-500/20', icon: Clock, label: 'Pending' },
    under_review: { color: 'text-blue-400', bgColor: 'bg-blue-500/10 border-blue-500/20', icon: Eye, label: 'Under Review' },
    resolved: { color: 'text-emerald-400', bgColor: 'bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle, label: 'Resolved' },
    rejected: { color: 'text-red-400', bgColor: 'bg-red-500/10 border-red-500/20', icon: XCircle, label: 'Rejected' },
  };
  return configs[status] || configs.pending;
};

const getPriorityConfig = (priority: string) => {
  const configs: Record<string, { color: string; bgColor: string; label: string }> = {
    low: { color: 'text-slate-400', bgColor: 'bg-slate-500/10', label: 'Low' },
    medium: { color: 'text-blue-400', bgColor: 'bg-blue-500/10', label: 'Medium' },
    high: { color: 'text-amber-400', bgColor: 'bg-amber-500/10', label: 'High' },
    urgent: { color: 'text-red-400', bgColor: 'bg-red-500/10', label: 'Urgent' },
  };
  return configs[priority] || configs.low;
};

export default function GameDisputes() {

  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [stats, setStats] = useState({
    pending: 0,
    underReview: 0,
    resolved: 0,
    rejected: 0,
    urgent: 0,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    gameType: '',
  });

  // Modals
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [resolution, setResolution] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedWinnerId, setSelectedWinnerId] = useState<string>('');
  const [isResolving, setIsResolving] = useState(false);

  const itemsPerPage = 10;

  const fetchDisputes = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (activeTab !== 'all') {
        params.status = activeTab;
      }
      if (filters.priority) {
        params.priority = filters.priority;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await gamesApi.getGameDisputes(params);
      const data = response.data;

      if (data.success && data.data) {
        const mappedDisputes: Dispute[] = (data.data.disputes || []).map((d: any) => ({
          _id: d._id,
          gameId: d.gameId,
          gameType: d.gameType,
          roomType: d.roomType,
          entryFee: d.entryFee,
          prizePool: d.prizePool,
          status: d.status,
          priority: d.priority,
          reason: d.reason,
          resolution: d.resolution,
          resolutionNote: d.resolutionNote,
          reporter: {
            _id: d.reporter?._id || d.reporter,
            name: d.reporter?.name || d.reporter?.username || d.reporter?.phone || 'Unknown User',
            phone: d.reporter?.phone || '',
            avatar: d.reporter?.avatar || null,
          },
          opponent: {
            _id: d.opponent?._id || d.opponent,
            name: d.opponent?.name || d.opponent?.username || d.opponent?.phone || 'Unknown User',
            phone: d.opponent?.phone || '',
            avatar: d.opponent?.avatar || null,
          },
          claimedWinner: d.claimedWinner?._id || d.claimedWinner,
          screenshots: (d.screenshots || []).map((s: any, i: number) => ({
            id: `ss_${i}`,
            url: s.url,
            uploadedBy: s.uploadedBy,
            uploadedAt: s.uploadedAt,
          })),
          messages: (d.messages || []).map((m: any, i: number) => ({
            id: `msg_${i}`,
            userId: m.userId,
            message: m.message,
            createdAt: m.createdAt,
          })),
          gameStartedAt: d.gameStartedAt,
          gameEndedAt: d.gameEndedAt,
          createdAt: d.createdAt,
          resolvedAt: d.resolvedAt,
          resolvedBy: d.resolvedBy?._id || d.resolvedBy,
          resolvedByName: d.resolvedBy?.name,
          assignedTo: d.assignedTo?._id || d.assignedTo,
          assignedToName: d.assignedTo?.name,
        }));

        setDisputes(mappedDisputes);
        setStats(data.data.stats || stats);
        setPagination({
          total: data.data.pagination?.total || mappedDisputes.length,
          pages: data.data.pagination?.pages || 1,
        });
      }
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
      toast.error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  }, [currentPage, activeTab, filters.priority, searchQuery]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  // Filter disputes locally for search
  const filteredDisputes = useMemo(() => {
    let filtered = [...disputes];

    // Additional filters
    if (filters.status) {
      filtered = filtered.filter((d) => d.status === filters.status);
    }
    if (filters.gameType) {
      filtered = filtered.filter((d) => d.gameType === filters.gameType);
    }

    return filtered;
  }, [disputes, filters]);

  const paginatedDisputes = filteredDisputes;

  const totalPages = pagination.pages;

  const handleRefresh = () => {
    fetchDisputes();
  };

  const handleReviewDispute = (dispute: Dispute) => {
    setSelectedDispute(dispute);
    setReviewModalOpen(true);
  };

  const handleResolveDispute = async () => {
    if (!resolution) {
      toast.error('Please select a resolution');
      return;
    }

    if (resolution === 'winner_confirmed' && !selectedWinnerId) {
      toast.error('Please select a winner');
      return;
    }

    if (!selectedDispute) return;

    setIsResolving(true);
    try {
      await gamesApi.resolveDispute(selectedDispute._id, {
        resolution,
        resolutionNote,
        winnerId: resolution === 'winner_confirmed' ? selectedWinnerId : undefined,
      });

      toast.success('Dispute resolved successfully');
      setReviewModalOpen(false);
      setSelectedDispute(null);
      setResolution('');
      setResolutionNote('');
      setSelectedWinnerId('');
      fetchDisputes();
    } catch (error: any) {
      console.error('Failed to resolve dispute:', error);
      toast.error(error.response?.data?.message || 'Failed to resolve dispute');
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
            Game Disputes
          </h1>
          <p className="text-slate-400 mt-1">Review and resolve game disputes with screenshot verification</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCcw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.pending}</p>
              <p className="text-xs text-slate-400">Pending</p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.underReview}</p>
              <p className="text-xs text-slate-400">Under Review</p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.resolved}</p>
              <p className="text-xs text-slate-400">Resolved</p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.rejected}</p>
              <p className="text-xs text-slate-400">Rejected</p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.urgent}</p>
              <p className="text-xs text-slate-400">Urgent</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={itemVariants}>
        <Tabs
          tabs={[
            { id: 'all', label: 'All Disputes', badge: disputes.length },
            { id: 'pending', label: 'Pending', badge: stats.pending },
            { id: 'under_review', label: 'Under Review', badge: stats.underReview },
            { id: 'resolved', label: 'Resolved' },
            { id: 'rejected', label: 'Rejected' },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
          variant="underline"
        />
      </motion.div>

      {/* Search and Filters */}
      <motion.div variants={itemVariants}>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by game ID, player name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                />
              </div>
              <Button
                variant={showFilters ? 'primary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </div>

            {showFilters && (
              <FilterPanel isOpen={showFilters} onClose={() => setShowFilters(false)}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  >
                    <option value="">Status</option>
                    <option value="pending">Pending</option>
                    <option value="under_review">Under Review</option>
                    <option value="resolved">Resolved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <select
                    value={filters.priority}
                    onChange={(e) => setFilters((prev) => ({ ...prev, priority: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  >
                    <option value="">Priority</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  <select
                    value={filters.gameType}
                    onChange={(e) => setFilters((prev) => ({ ...prev, gameType: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  >
                    <option value="">Game Type</option>
                    <option value="ludo">Ludo</option>
                  </select>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => setFilters({ status: '', priority: '', gameType: '' })}
                >
                  Clear Filters
                </Button>
              </FilterPanel>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Disputes Table */}
      <motion.div variants={itemVariants}>
        <Card variant="glass">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Game</TableHead>
                  <TableHead>Players</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Screenshots</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedDisputes.map((dispute) => {
                  const statusConfig = getStatusConfig(dispute.status);
                  const priorityConfig = getPriorityConfig(dispute.priority);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <TableRow key={dispute._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                            <Gamepad2 className="w-5 h-5 text-violet-400" />
                          </div>
                          <div>
                            <p className="font-mono text-sm text-white">{dispute.gameId}</p>
                            <p className="text-xs text-slate-500 capitalize">{dispute.gameType} • {dispute.roomType}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center text-white text-xs font-medium">
                              {getInitials(dispute.reporter.name)}
                            </div>
                            <span className="text-sm text-slate-300">{dispute.reporter.name}</span>
                            <span className="text-xs text-amber-400">(Reporter)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-slate-600 to-slate-500 flex items-center justify-center text-white text-xs font-medium">
                              {getInitials(dispute.opponent.name)}
                            </div>
                            <span className="text-sm text-slate-400">{dispute.opponent.name}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm text-white">{formatCurrency(dispute.prizePool)}</p>
                          <p className="text-xs text-slate-500">Entry: {formatCurrency(dispute.entryFee)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border',
                          statusConfig.bgColor,
                          statusConfig.color
                        )}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {statusConfig.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          'px-2.5 py-1 rounded-lg text-xs font-medium',
                          priorityConfig.bgColor,
                          priorityConfig.color
                        )}>
                          {priorityConfig.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <ImageIcon className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-300">{dispute.screenshots.length}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-300">{formatRelativeTime(dispute.createdAt)}</p>
                        <p className="text-xs text-slate-500">{formatDate(dispute.createdAt)}</p>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu
                          trigger={
                            <button className="p-2 rounded-lg hover:bg-slate-800 transition-colors">
                              <MoreHorizontal className="w-4 h-4 text-slate-400" />
                            </button>
                          }
                          items={[
                            {
                              label: 'Review Dispute',
                              icon: Eye,
                              onClick: () => handleReviewDispute(dispute),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {filteredDisputes.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredDisputes.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Review Modal */}
      <Modal
        isOpen={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setSelectedDispute(null);
          setResolution('');
          setResolutionNote('');
          setSelectedImage(null);
        }}
        title="Review Game Dispute"
        size="xl"
      >
        {selectedDispute && (
          <div className="space-y-6">
            {/* Game Info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-3 rounded-xl bg-slate-800/50 text-center">
                <p className="text-sm text-slate-400">Game ID</p>
                <p className="font-mono text-white">{selectedDispute.gameId}</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-800/50 text-center">
                <p className="text-sm text-slate-400">Prize Pool</p>
                <p className="text-lg font-bold text-emerald-400">{formatCurrency(selectedDispute.prizePool)}</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-800/50 text-center">
                <p className="text-sm text-slate-400">Game Type</p>
                <p className="text-white capitalize">{selectedDispute.gameType}</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-800/50 text-center">
                <p className="text-sm text-slate-400">Room Type</p>
                <p className="text-white">{selectedDispute.roomType}</p>
              </div>
            </div>

            {/* Players */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <p className="text-xs text-amber-400 mb-2">Reporter (Claims to have won)</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center text-white font-medium">
                    {getInitials(selectedDispute.reporter.name)}
                  </div>
                  <div>
                    <p className="font-medium text-white">{selectedDispute.reporter.name}</p>
                    <p className="text-xs text-slate-400">{selectedDispute.reporter.phone}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/50">
                <p className="text-xs text-slate-400 mb-2">Opponent</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-500 flex items-center justify-center text-white font-medium">
                    {getInitials(selectedDispute.opponent.name)}
                  </div>
                  <div>
                    <p className="font-medium text-white">{selectedDispute.opponent.name}</p>
                    <p className="text-xs text-slate-400">{selectedDispute.opponent.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reason */}
            <div className="p-4 rounded-xl bg-slate-800/50">
              <p className="text-sm text-slate-400 mb-2">Dispute Reason</p>
              <p className="text-white">{selectedDispute.reason}</p>
            </div>

            {/* Screenshots */}
            <div>
              <p className="text-sm text-slate-400 mb-3">Screenshots ({selectedDispute.screenshots.length})</p>
              {selectedDispute.screenshots.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {selectedDispute.screenshots.map((ss) => (
                    <div
                      key={ss.id}
                      className="relative aspect-video rounded-xl bg-slate-800 overflow-hidden cursor-pointer group"
                      onClick={() => setSelectedImage(ss.url)}
                    >
                      {ss.url ? (
                        <img
                          src={ss.url}
                          alt="Screenshot"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Show placeholder on error
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-slate-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <ZoomIn className="w-6 h-6 text-white" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-xs text-white truncate">
                          By: {ss.uploadedBy === selectedDispute.reporter._id ? 'Reporter' : 'Opponent'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 rounded-xl bg-slate-800/50 text-center">
                  <ImageIcon className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">No screenshots uploaded</p>
                </div>
              )}
            </div>

            {/* Messages */}
            {selectedDispute.messages.length > 0 && (
              <div>
                <p className="text-sm text-slate-400 mb-3">Messages</p>
                <div className="space-y-3 max-h-48 overflow-y-auto p-2">
                  {selectedDispute.messages.map((msg) => {
                    const isReporter = msg.userId === selectedDispute.reporter._id;
                    return (
                      <div
                        key={msg.id}
                        className={cn(
                          'p-3 rounded-xl max-w-[80%]',
                          isReporter
                            ? 'bg-amber-500/10 border border-amber-500/20 ml-0'
                            : 'bg-slate-800/50 ml-auto'
                        )}
                      >
                        <p className="text-xs text-slate-500 mb-1">
                          {isReporter ? 'Reporter' : 'Opponent'} • {formatRelativeTime(msg.createdAt)}
                        </p>
                        <p className="text-sm text-white">{msg.message}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Resolution */}
            {(selectedDispute.status === 'pending' || selectedDispute.status === 'under_review') && (
              <div className="space-y-4 pt-4 border-t border-slate-800">
                <div>
                  <p className="text-sm text-slate-400 mb-2">Resolution Decision</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setResolution('winner_confirmed')}
                      className={cn(
                        'p-3 rounded-xl border-2 transition-all',
                        resolution === 'winner_confirmed'
                          ? 'border-emerald-500 bg-emerald-500/10'
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      )}
                    >
                      <ThumbsUp className={cn(
                        'w-5 h-5 mx-auto mb-1',
                        resolution === 'winner_confirmed' ? 'text-emerald-400' : 'text-slate-400'
                      )} />
                      <p className="text-sm text-white">Confirm Winner</p>
                    </button>
                    <button
                      onClick={() => setResolution('refund_both')}
                      className={cn(
                        'p-3 rounded-xl border-2 transition-all',
                        resolution === 'refund_both'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      )}
                    >
                      <DollarSign className={cn(
                        'w-5 h-5 mx-auto mb-1',
                        resolution === 'refund_both' ? 'text-blue-400' : 'text-slate-400'
                      )} />
                      <p className="text-sm text-white">Refund Both</p>
                    </button>
                  </div>
                </div>

                {/* Winner Selection - only show when winner_confirmed is selected */}
                {resolution === 'winner_confirmed' && (
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Select Winner</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setSelectedWinnerId(selectedDispute.reporter._id)}
                        className={cn(
                          'p-3 rounded-xl border-2 transition-all text-left',
                          selectedWinnerId === selectedDispute.reporter._id
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center text-white text-xs font-medium">
                            {getInitials(selectedDispute.reporter.name)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">{selectedDispute.reporter.name}</p>
                            <p className="text-xs text-slate-500">Reporter</p>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => setSelectedWinnerId(selectedDispute.opponent._id)}
                        className={cn(
                          'p-3 rounded-xl border-2 transition-all text-left',
                          selectedWinnerId === selectedDispute.opponent._id
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-600 to-slate-500 flex items-center justify-center text-white text-xs font-medium">
                            {getInitials(selectedDispute.opponent.name)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">{selectedDispute.opponent.name}</p>
                            <p className="text-xs text-slate-500">Opponent</p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Resolution Note</label>
                  <textarea
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                    placeholder="Add notes about your decision..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setReviewModalOpen(false);
                      setSelectedDispute(null);
                      setResolution('');
                      setResolutionNote('');
                      setSelectedWinnerId('');
                    }}
                    disabled={isResolving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1"
                    onClick={handleResolveDispute}
                    disabled={!resolution || isResolving || (resolution === 'winner_confirmed' && !selectedWinnerId)}
                  >
                    {isResolving ? (
                      <>
                        <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve Dispute
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Already Resolved */}
            {(selectedDispute.status === 'resolved' || selectedDispute.status === 'rejected') && (
              <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-400">Resolution</p>
                  <span className={cn(
                    'px-2 py-0.5 rounded-md text-xs font-medium',
                    selectedDispute.status === 'resolved'
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-red-500/10 text-red-400'
                  )}>
                    {selectedDispute.resolution?.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="text-white">{selectedDispute.resolutionNote}</p>
                <p className="text-xs text-slate-500 mt-2">
                  Resolved by {selectedDispute.resolvedByName} on {formatDateTime(selectedDispute.resolvedAt!)}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Image Viewer Modal */}
      <Modal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        title="Screenshot"
        size="xl"
      >
        <div className="relative bg-slate-900 rounded-xl overflow-hidden flex items-center justify-center min-h-[300px]">
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Screenshot"
              className="max-w-full max-h-[70vh] object-contain"
            />
          ) : (
            <>
              <ImageIcon className="w-16 h-16 text-slate-700" />
              <p className="absolute bottom-4 left-4 text-sm text-slate-400">
                No image to display
              </p>
            </>
          )}
        </div>
      </Modal>
    </motion.div>
  );
}
